import styled from '@emotion/styled';
import { HomeyAPIV3Local } from 'homey-api';
import { useRef, useState } from 'react';
import { useTextField, useButton } from 'react-aria';

import config from '../config.json';
import { history } from '../history';
import { ToastManager } from '../ToastManager';

import { getLocalBaseUrl } from '../store/SelectedHomeyStore';

import { su } from '../theme/functions/su';
import { glyph } from '../theme/glyph';
import { theme } from '../theme/theme';
import styles from './LocalLoginPage.module.css';

import { Glyph } from '../components/common/Glyph';
import { Throbber } from '../components/common/Throbber';

export function LocalLoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const usernameTextFieldRef = useRef();
  const usernameTextField = useTextField(
    {
      inputMode: 'text',
      'aria-label': 'Username',
      type: 'text',
      name: 'username',
      placeholder: 'Username',
      autoFocus: true,
      onChange: setUsername,
      value: username,
    },
    usernameTextFieldRef,
  );

  const passwordTextFieldRef = useRef();
  const passwordTextField = useTextField(
    {
      inputMode: 'text',
      'aria-label': 'Password',
      type: 'password',
      name: 'password',
      placeholder: 'Password',
      onChange: setPassword,
      value: password,
    },
    passwordTextFieldRef,
  );

  return (
    <S.Root>
      <div className={styles.PageHomeAnim} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
        }}
      >
        <form
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: su(1),
          }}
          onSubmit={(event) => {
            event.preventDefault();

            if (!username) return;
            if (!password) return;

            if (isLoading) {
              return;
            }

            setIsLoading(true);
            const baseUrl = getLocalBaseUrl();

            const apiLocal = new HomeyAPIV3Local({
              baseUrl: baseUrl,
              strategy: [],
              reconnect: false,
              properties: {},
            });

            const url = new URL(baseUrl);

            let apiHomeyLocal = apiLocal;

            if (url.protocol === 'http:' && /^\d{1,3}(\.\d{1,3}){3}$/.test(url.hostname)) {
              const newHost = `https://${url.hostname.replace(/\./g, '-')}.homey.homeylocal.com`;
              apiHomeyLocal = new HomeyAPIV3Local({
                baseUrl: newHost,
                strategy: [],
                reconnect: false,
                properties: {},
              });
            }

            const minWait = new Promise((resolve) => setTimeout(resolve, 1000));

            function onToken(token) {
              sessionStorage.setItem('HOMEY_LOCAL_TOKEN', token);

              if (
                history.location.state?.from != null &&
                history.location.state?.from !== `${import.meta.env.BASE_URL}login-local`
              ) {
                window.location.href = history.location.state.from;
                return;
              }

              window.location.href = `${import.meta.env.BASE_URL}`;
            }

            apiHomeyLocal
              .call({
                method: 'POST',
                path: `/api/manager/users/login-local`,
                body: {
                  username,
                  password,
                  clientName: 'Homey Web App',
                  clientId: config.clientId,
                },
                timeout: 2 * 1000,
              })
              .then(async (token) => {
                await minWait;
                onToken(token);
              })
              .catch(async (error) => {
                await apiLocal
                  .call({
                    method: 'POST',
                    path: `/api/manager/users/login-local`,
                    body: {
                      username,
                      password,
                      clientName: 'Homey Web App',
                      clientId: config.clientId,
                    },
                    timeout: 5 * 1000,
                  })
                  .then(async (token) => {
                    await minWait;
                    onToken(token);
                  });
              })
              .catch(async (error) => {
                await minWait;
                ToastManager.handleError(error);
                setIsLoading(false);
              });
          }}
        >
          <S.AvatarWrapper>
            <Glyph source={glyph.core.personFill} color={theme.color.white} size="40px" />
          </S.AvatarWrapper>
          <S.Input {...usernameTextField.inputProps} ref={usernameTextFieldRef} />
          <S.Input {...passwordTextField.inputProps} ref={passwordTextFieldRef} />
          <SignInButton isLoading={isLoading} isDisabled={!username || !password} label="Sign In" />
        </form>
      </div>
    </S.Root>
  );
}

function S() {}
LocalLoginPage.S = S;

S.Root = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

S.Input = styled.input`
  width: 320px;
  height: ${su(5)};
  border-radius: 5px;
  background-color: ${theme.color.white_o_20};
  border: 1px solid transparent;
  backdrop-filter: blur(30px);
  padding-inline: ${su(1)};
  color: ${theme.color.white};

  &::placeholder {
    color: ${theme.color.white};
    opacity: 0.5;
  }

  &:focus {
    border: 1px solid ${theme.color.white};
  }
`;

S.AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  margin-bottom: ${su(1)};
  border-radius: ${su(4)};
  background-color: ${theme.color.white_o_20};
  box-shadow: ${theme.boxShadow.default};
  backdrop-filter: blur(30px);
`;

S.SignInButton = styled.div`
  display: block;
  position: relative;
  width: 100px;
  height: ${su(5)};
  margin: ${su(1, 0)};
  line-height: 40px;
  background-color: white;
  border-radius: ${theme.borderRadius.large};
  color: black;
  mix-blend-mode: screen;
  transition:
    width 0.3s,
    opacity 0.2s;
  cursor: pointer;

  &[data-is-disabled='true'] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &[data-is-loading='true'] {
    width: ${su(5)};
  }
`;

S.SignInButtonLabel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;

  background-image: url('${glyph.core.chevronRightBold}');
  background-size: 12px 12px;
  background-repeat: no-repeat;
  background-position: right 15px center;

  padding-right: ${su(1)};
  opacity: 1;

  [data-is-loading='true'] & {
    opacity: 0;
    transition: opacity 0.3s;
  }
`;

S.SignInButtonThrobber = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 20px;
  height: 20px;
  line-height: 0;
  transform: translate(-50%, -50%);
  opacity: 0;

  [data-is-loading='true'] & {
    opacity: 1;
    transition: opacity 0.3s;
  }
`;

export function SignInButton(props) {
  const buttonRef = useRef();
  const button = useButton(
    {
      type: 'submit',
      'aria-label': props.label,
      isDisabled: props.isDisabled,
    },
    buttonRef,
  );

  return (
    <button type="submit" ref={buttonRef} {...button.buttonProps}>
      <S.SignInButton data-is-loading={props.isLoading} data-is-disabled={props.isDisabled}>
        <S.SignInButtonLabel>{props.label}</S.SignInButtonLabel>
        <S.SignInButtonThrobber>
          <Throbber size="20px" />
        </S.SignInButtonThrobber>
      </S.SignInButton>
    </button>
  );
}
